import React, { ReactElement, useEffect, useState } from 'react'
import styles from './Settings.module.scss'
import OauthIntegration from './oauth-integration/OauthIntegration'
import SignInSettings from './signin-settings/SignInSettings'
import { Translate } from 'react-localize-redux'
import { withPermissionsHOC } from 'core/hoc/permissionsHoc'
import { IAccessScope, IFeatures, IPermission, ITenant } from 'reducers/Reducers.interfaces'
import { IActionCreatorAsync } from 'core/actions/Actions.interfaces'
import { connect } from 'react-redux'
import { IAppState } from 'core/store/store'
import { fetchTenantById } from '../tenants/Tenants.actions'
import { ApplicationPermissionMap, IPermissions } from 'core/selectors/Profile.selectors'
import { Accordion, JumpNav, Spinner } from '@digicert/dcone-common-ui'
import { translate } from '../../shared/helpers/utils'
import LicenseDetails from './license-details/LicenseDetails'
import { IAccordionItem } from 'secure-pages/tenants/tenant/Tenant.types'

export interface ISettingsState {
  tenant?: ITenant,
  loading: boolean,
  disableAddIntegration: boolean

}

export interface ISettingsStateProps {
  primaryAccountId: string
  permissions?: IPermission[]
  features: IFeatures
  accessScope?: IAccessScope
}

interface ISettingsProps {
  fetchTenantById: IActionCreatorAsync
  tenant?: ITenant
  permissions: IPermissions
  primaryAccountId: string
  features: IFeatures
  accessScope?: IAccessScope
}

const Settings = (props: ISettingsProps) => {
  const { permissions, fetchTenantById, primaryAccountId, features, accessScope } = props
  const [tenant, setTenant] = useState<ITenant|undefined>(props.tenant)
  const [loading, setLoading] = useState<boolean>(true)

  const getAccordionData = (): IAccordionItem[] => {
    const tempAccordionData: IAccordionItem[] = []
    if (permissions) {
      const mapPermissions = Object.values(permissions)
      const canManageSystemSettings = mapPermissions.find(permission => permission.name === ApplicationPermissionMap.canManageSystemSettings)
      const canViewAMLicense = mapPermissions.find(permission => permission.name === ApplicationPermissionMap.canViewAMLicense)
      const canManageAMLicense = mapPermissions.find(permission => permission.name === ApplicationPermissionMap.canManageAMLicense)
      if (canManageSystemSettings) {
        tempAccordionData.push({
          headline: translate('oauthIntegration.integrationHeadline') as string,
          key: 'oauthIntegration',
          view: <OauthIntegration removeAddIntegration={features.enableAddServicesIntegration} />,
          link: '#',
          anchorId: 'oauthIntegration'
        })
        tempAccordionData.push({
          headline: translate('platformSignInSettings.title') as string,
          key: 'platformSignInSettings',
          view: <SignInSettings
            // todo: Type ITenant | undefined is not assignable to type ITenant
            //@ts-ignore
            tenant={tenant}
            primaryAccountId={primaryAccountId}
            canEdit={canManageSystemSettings}
            accountDetailsPage={false}
            accessScope={accessScope}
            fromSettings={window.location.pathname.split('/')[3] === 'settings'}
          />,
          link: '#',
          anchorId: 'platformSignInSettings'
        })
      }
      if (canViewAMLicense || canManageAMLicense) {
        tempAccordionData.push({
          headline: translate('licenseDetails.title') as string,
          key: 'licenseDetails',
          view: <LicenseDetails />,
          link: '#',
          anchorId: 'licenseDetails'
        })
      }
    }
    return tempAccordionData
  }

  const getAccordionActiveKeys = () => {
    return getAccordionData().map((data) => data.key)
  }

  const getParentContainer = (): HTMLElement | Window => {
    return document.getElementById('content_wrap') || window
  }

  const getJumpNavData = () => {
    return getAccordionData().map((data) => {
      return {
        url: `#${data.key}`,
        title: data.headline
      }
    })
  }

  useEffect(() => {
    if (permissions) {
      const mapPermissions = Object.values(permissions)
      const canManageSystemSettings = mapPermissions.find(permission => permission.name === ApplicationPermissionMap.canManageSystemSettings)
      if (canManageSystemSettings) {
        fetchTenantById(primaryAccountId).then((tenant: any) => {
          if (tenant) {
            setTenant(tenant)
            setLoading(false)
          }
        })
      } else {
        setLoading(false)
      }
    }
  },[])

  if (loading) {
    return (
      <div className='spin-wrap'>
        <Spinner/>
      </div>
    )
  }

  if (!loading) {
    return (
      <section className={styles.settings}>
        <header className={styles.settingsHeader}>
          <h1><Translate id={'oauthIntegration.headline'} /></h1>
        </header>
        <div className={styles.sectionsSideNavWrap}>
          <div className={styles.sections}>
            <Accordion
              activeKey={getAccordionActiveKeys()}
              oneByOne={false}
              accordionData={getAccordionData()}
            />
          </div>
          <div className={styles.sideNav}>
            <JumpNav
              headline={translate('common.pageNavTitle') as string}
              bounds={100}
              displayHash={false}
              topSpacing={10}
              getScrollContainer={getParentContainer}
              items={getJumpNavData()}
            />
          </div>
        </div>
      </section>
    )
  }
}

export default connect(
  (state: IAppState): ISettingsStateProps => ({
    primaryAccountId: state.profile.data !== undefined ? state.profile.data.primary_account_id : '',
    permissions: state.profile.data?.permissions,
    features: state.features,
    accessScope: state.profile.data !== undefined ? state.profile.data.access_scope : undefined
  }),
  { fetchTenantById }
)(withPermissionsHOC(Settings, []))
